import { Logger } from '../../../../Arshu/dist/logger/logger.js';
import { JsonObject, JsonValue } from '../../app/json/jsonValue.js';
import { JsonUtility } from '../../app/jsonUtility.js';
import { DynamicConfigMap } from '../../registry/dynamicConfigMap.js';
import { ArtifactKind } from '../../config/iConfig.js';
import { DeserializePreProcessMetadata } from './deserializePreProcessMetadata.js';
import { PreProcessTemplateEntry } from '../../model/index.js';
import type { IConfig } from '../../config/iConfig.js';
import type { LoaderPreProcess } from './loaderPreProcess.js';

type CachedTemplateMap = Map<string, PreProcessTemplateEntry>;

export const lifecycleMethods = {
    load(this: LoaderPreProcess, config: IConfig, appSite: string): boolean {
        try {
            const searchAppSites = config.getSearchAppSitesForAppSite(appSite);
            Logger.debug(`[LOAD] Called for appSite: ${appSite}, searchAppSites: ${searchAppSites}`, 'LoaderPreProcess');

            this._config = config;
            this._appSite = appSite;

            // Client/OCI mode: the source may provide pre-built Preprocessed artifacts — the server-computed
            // template structure (resolved replacement mappings included). Rehydrate it instead of re-parsing
            // raw HTML. This is the WHOLE point of PreProcess: the expensive parse happens once on the server
            // and the client rehydrates. Mirrors LoaderPreProcessJson's pre-built Metadata branch. When no
            // Preprocessed artifacts exist (filesystem source), fall through to the build-from-raw path.
            if (lifecycleMethods.tryRehydratePreprocessedFromArtifacts.call(this, config, appSite, searchAppSites)) {
                this._preloadDynamicAndGlobalData(config, appSite);
                Logger.info(`Rehydrated preprocessed templates for ${appSite} from artifacts`, 'LoaderPreProcess');
                return true;
            }

            let templates: CachedTemplateMap;

            if (this._appSiteCache.has(this._appSite)) {
                Logger.debug(`[LOAD] Cache HIT for ${appSite}, checking if has mappings`, 'LoaderPreProcess');

                const cached = this._appSiteCache.get(this._appSite)!;
                const mappingsComplete = this._hasCompleteReplacementMappings(cached.templates);

                Logger.debug(`[LOAD] Cached templates for ${appSite} have complete mappings: ${mappingsComplete}`, 'LoaderPreProcess');

                if (mappingsComplete) {
                    Logger.debug(`[LOAD] Returning cached templates with mappings for ${appSite} (${cached.templates.size} templates)`, 'LoaderPreProcess');
                    return true;
                }

                Logger.debug(`[LOAD] Cache has NO mappings, will regenerate for ${appSite}`, 'LoaderPreProcess');
                templates = cached.templates;
            } else {
                Logger.debug(`[LOAD] Cache MISS for ${appSite}, loading from disk`, 'LoaderPreProcess');
                templates = (this.constructor as typeof LoaderPreProcess).loadTemplatesFromSingleAppSite(config, appSite);
                this._appSiteCache.set(this._appSite, { templates, parentMap: null });
            }

            if (searchAppSites) {
                const searchAppSitesArray = searchAppSites.split(',');
                for (const searchAppSite of searchAppSitesArray) {
                    const trimmedSite = searchAppSite.trim();
                    if (!trimmedSite) continue;

                    if (!this._appSiteCache.has(trimmedSite)) {
                        const searchTemplates = (this.constructor as typeof LoaderPreProcess).loadTemplatesFromSingleAppSite(config, trimmedSite);
                        this._appSiteCache.set(trimmedSite, { templates: searchTemplates, parentMap: null });
                        Logger.debug(`Loaded searchAppSite '${trimmedSite}' with ${searchTemplates.size} templates`, 'LoaderPreProcess');
                    }
                }
            }

            Logger.debug(`[LOAD] Creating replacement mappings for primary appSite ${appSite} with ${templates.size} templates`, 'LoaderPreProcess');
            templates = this._createAllReplacementMappingsWithCache(templates, appSite);
            Logger.debug(`[LOAD] Replacement mappings created, updating cache`, 'LoaderPreProcess');
            const cached = this._appSiteCache.get(this._appSite);
            this._appSiteCache.set(this._appSite, { templates, parentMap: cached?.parentMap || null });

            if (searchAppSites) {
                const searchAppSitesArray = searchAppSites.split(',');
                for (const searchAppSite of searchAppSitesArray) {
                    const trimmedSite = searchAppSite.trim();
                    if (!trimmedSite) continue;

                    const searchCached = this._appSiteCache.get(trimmedSite);
                    if (searchCached) {
                        const searchTemplates = this._createAllReplacementMappingsWithCache(searchCached.templates, trimmedSite);
                        this._appSiteCache.set(trimmedSite, { templates: searchTemplates, parentMap: searchCached.parentMap });
                        Logger.debug(`Created mappings for searchAppSite '${trimmedSite}'`, 'LoaderPreProcess');
                    }
                }
            }

            this._preloadDynamicAndGlobalData(config, appSite);

            Logger.info(`Loaded ${templates.size} templates for ${appSite}`, 'LoaderPreProcess');
            return true;
        } catch (ex) {
            Logger.error(`Failed to load templates: ${(ex as Error).message}`, 'LoaderPreProcess');
            Logger.fallback("F-ENG2", `load templates failed: ex=${(ex as Error).message}`);
            throw ex;
        }
    },

    /**
     * Client/OCI rehydration: when the source carries pre-built Preprocessed artifacts, decode each
     * AppSite's server-computed PreprocessedTemplate set (resolved replacement mappings included) and
     * store it directly — skipping per-template preprocessTemplate AND cross-template
     * _createAllReplacementMappingsWithCache (the artifact already holds the resolved mappings).
     * Returns false when the primary AppSite has no Preprocessed artifacts (filesystem source) so load()
     * falls through to the build-from-raw path. Mirrors LoaderPreProcessJson's Metadata branch.
     */
    tryRehydratePreprocessedFromArtifacts(this: LoaderPreProcess, config: IConfig, appSite: string, searchAppSites: string): boolean {
        const primaryNames = config.getArtifactNames(appSite, ArtifactKind.Preprocessed);
        if (primaryNames.length === 0) {
            return false;
        }

        const appSitesToLoad = (this.constructor as typeof LoaderPreProcess).BuildAppSitesToLoad(appSite, searchAppSites);
        for (const site of appSitesToLoad) {
            if (this._appSiteCache.has(site)) {
                continue;
            }

            const names = site.toLowerCase() === appSite.toLowerCase()
                ? primaryNames
                : config.getArtifactNames(site, ArtifactKind.Preprocessed);

            const rehydrated: CachedTemplateMap = new Map();
            for (const name of names) {
                // `names` came from this source's OWN getArtifactNames index, so a null here is the
                // source contradicting itself — never a licence to skip the template (which would
                // silently drop it from the store and render DefaultReplaceHtml).
                const bytes = config.getArtifactBytes(site, name, ArtifactKind.Preprocessed);
                if (bytes == null) {
                    Logger.fallback('F-ENG7',
                        `Preprocessed artifact enumerated but not served: appSite=${site} contentName=${name}`);
                    throw new Error(
                        `F-ENG7: Preprocessed artifact missing for '${site}/${name}' though the source enumerated it.`);
                }

                // Bytes present means the artifact EXISTS, so a decode/parse failure is a corrupt
                // artifact. The previous `catch { continue; }` swallowed that and dropped the
                // template; parseJsonString would also have handed an EMPTY object to deserialize.
                let parsed: JsonObject;
                try {
                    parsed = JsonUtility.parseJsonStringStrict(JsonUtility.decodeUtf8Strict(bytes));
                } catch (ex) {
                    Logger.fallback('F-ENG6',
                        `Preprocessed artifact present but undecodable/unparseable: appSite=${site} contentName=${name} bytes=${bytes.length} ex=${(ex as Error).message}`);
                    throw ex;
                }

                const template = DeserializePreProcessMetadata.deserialize(parsed);
                const key = `${site.toLowerCase()}_${name.toLowerCase()}`;
                const json = config.getDataJson(site, name);
                rehydrated.set(key, new PreProcessTemplateEntry(template, json));
            }

            this._appSiteCache.set(site, { templates: rehydrated, parentMap: null });
            Logger.debug(`Rehydrated ${rehydrated.size} preprocessed templates for ${site}`, 'LoaderPreProcess');
        }

        return true;
    },

    _preloadDynamicAndGlobalData(this: LoaderPreProcess, config: IConfig, appSite: string): void {
        Logger.flow(`event=preloadData phase=PerScenario engine=PreProcess appSite=${appSite}`, "Lifecycle");
        const allAppSites = [appSite];
        const searchAppSites = config.getSearchAppSitesForAppSite(appSite);
        if (searchAppSites) {
            for (const s of searchAppSites.split(',')) {
                const trimmed = s.trim();
                if (trimmed && !allAppSites.includes(trimmed)) {
                    allAppSites.push(trimmed);
                }
            }
        }

        for (const site of allAppSites) {
            const ddFolderJson = config.getFolderDataJson(site, 'dynamicdata');
            const ddAppSiteJson = config.getDataJson(site, 'dynamicdata');
            let ddConfig: DynamicConfigMap | null = null;
            const ddFolderConfig = DynamicConfigMap.parseDynamicConfig(ddFolderJson, 'Data');
            const ddAppSiteConfig = DynamicConfigMap.parseDynamicConfig(ddAppSiteJson, 'Data');
            if (ddFolderConfig) ddConfig = ddFolderConfig;
            if (ddAppSiteConfig) {
                if (!ddConfig) ddConfig = ddAppSiteConfig;
                else DynamicConfigMap.mergeDynamicConfig(ddConfig, ddAppSiteConfig);
            }
            this._dynamicDataConfigStore[site] = ddConfig;

            if (ddConfig) {
                for (const [, handlers] of ddConfig) {
                    if (!Array.isArray(handlers)) continue;
                    for (const handler of handlers) {
                        const paramFile = handler.iParamFile;
                        if (!paramFile) continue;
                        const storeKey = `${site}:${paramFile}`;
                        if (this._dynamicDataParamsStore.containsKey(storeKey)) continue;
                        const fileName = paramFile.replace(/DynamicData\./i, 'dynamicdata.');
                        const paramJson = config.getDataJson(site, fileName) || config.getFolderDataJson(site, fileName);
                        if (paramJson) this._dynamicDataParamsStore.set(storeKey, JsonValue.object(paramJson));
                    }
                }
            }

            // CLIENT-DATA preparer bindings (dynamicclientdata.json) — parsed like dynamicdata.json but kept
            // in a SEPARATE store so client-data handlers never mix into the server IData path. "Data" legacy
            // key fallback is harmless here (bindings use unified IType/IParamFile + the DataSource set).
            const cdFolderJson = config.getFolderDataJson(site, 'dynamicclientdata');
            const cdAppSiteJson = config.getDataJson(site, 'dynamicclientdata');
            let cdConfig: DynamicConfigMap | null = null;
            const cdFolderConfig = DynamicConfigMap.parseDynamicConfig(cdFolderJson, 'Data');
            const cdAppSiteConfig = DynamicConfigMap.parseDynamicConfig(cdAppSiteJson, 'Data');
            if (cdFolderConfig) cdConfig = cdFolderConfig;
            if (cdAppSiteConfig) {
                if (!cdConfig) cdConfig = cdAppSiteConfig;
                else DynamicConfigMap.mergeDynamicConfig(cdConfig, cdAppSiteConfig);
            }
            this._dynamicClientDataConfigStore[site] = cdConfig;

            if (cdConfig) {
                for (const [, handlers] of cdConfig) {
                    if (!Array.isArray(handlers)) continue;
                    for (const handler of handlers) {
                        const paramFile = handler.iParamFile;
                        if (!paramFile) continue;
                        const storeKey = `${site}:${paramFile}`;
                        if (this._dynamicClientDataParamsStore.containsKey(storeKey)) continue;
                        const fileName = paramFile.replace(/DynamicClientData\./i, 'dynamicclientdata.');
                        const paramJson = config.getDataJson(site, fileName) || config.getFolderDataJson(site, fileName);
                        if (paramJson) this._dynamicClientDataParamsStore.set(storeKey, JsonValue.object(paramJson));
                    }
                }
            }

            const dvFolderJson = config.getFolderDataJson(site, 'dynamicview');
            const dvAppSiteJson = config.getDataJson(site, 'dynamicview');
            let dvConfig: DynamicConfigMap | null = null;
            const dvFolderConfig = DynamicConfigMap.parseDynamicConfig(dvFolderJson, 'View');
            const dvAppSiteConfig = DynamicConfigMap.parseDynamicConfig(dvAppSiteJson, 'View');
            if (dvFolderConfig) dvConfig = dvFolderConfig;
            if (dvAppSiteConfig) {
                if (!dvConfig) dvConfig = dvAppSiteConfig;
                else DynamicConfigMap.mergeDynamicConfig(dvConfig, dvAppSiteConfig);
            }
            this._dynamicViewConfigStore[site] = dvConfig;

            if (dvConfig) {
                for (const [, handlers] of dvConfig) {
                    if (!Array.isArray(handlers)) continue;
                    for (const handler of handlers) {
                        const paramFile = handler.iParamFile;
                        if (!paramFile) continue;
                        const storeKey = `${site}:${paramFile}`;
                        if (this._dynamicViewParamsStore.containsKey(storeKey)) continue;
                        const fileName = paramFile.replace(/DynamicView\./i, 'dynamicview.');
                        const paramJson = config.getDataJson(site, fileName) || config.getFolderDataJson(site, fileName);
                        if (paramJson) this._dynamicViewParamsStore.set(storeKey, JsonValue.object(paramJson));
                    }
                }
            }

            const daFolderJson = config.getFolderDataJson(site, 'dynamicaction');
            const daAppSiteJson = config.getDataJson(site, 'dynamicaction');
            let daConfig: DynamicConfigMap | null = null;
            const daFolderConfig = DynamicConfigMap.parseDynamicConfig(daFolderJson, 'Action');
            const daAppSiteConfig = DynamicConfigMap.parseDynamicConfig(daAppSiteJson, 'Action');
            if (daFolderConfig) daConfig = daFolderConfig;
            if (daAppSiteConfig) {
                if (!daConfig) daConfig = daAppSiteConfig;
                else DynamicConfigMap.mergeDynamicConfig(daConfig, daAppSiteConfig);
            }
            this._dynamicActionConfigStore[site] = daConfig;

            if (daConfig) {
                for (const [, handlers] of daConfig) {
                    if (!Array.isArray(handlers)) continue;
                    for (const handler of handlers) {
                        const paramFile = handler.iParamFile;
                        if (!paramFile) continue;
                        const storeKey = `${site}:${paramFile}`;
                        if (this._dynamicActionParamsStore.containsKey(storeKey)) continue;
                        const fileName = paramFile.replace(/DynamicAction\./i, 'dynamicaction.');
                        const paramJson = config.getDataJson(site, fileName) || config.getFolderDataJson(site, fileName);
                        if (paramJson) this._dynamicActionParamsStore.set(storeKey, JsonValue.object(paramJson));
                    }
                }
            }

            // DynamicAppSite handlers (AppSite-triggered). Mirrors C# loader lifecycle: the wwwroot-level
            // wildcard ("*") layer (getRootJson) is least-specific; folder then appsite override it.
            const dsRootJson = config.getRootJson('dynamicappsite.json');
            const dsFolderJson = config.getFolderDataJson(site, 'dynamicappsite');
            const dsAppSiteJson = config.getDataJson(site, 'dynamicappsite');
            let dsConfig: DynamicConfigMap | null = null;
            const dsRootConfig = (dsRootJson && dsRootJson.size > 0) ? DynamicConfigMap.parseDynamicConfig(dsRootJson, 'AppSite') : null;
            const dsFolderConfig = DynamicConfigMap.parseDynamicConfig(dsFolderJson, 'AppSite');
            const dsAppSiteConfig = DynamicConfigMap.parseDynamicConfig(dsAppSiteJson, 'AppSite');
            if (dsRootConfig) dsConfig = dsRootConfig;
            if (dsFolderConfig) {
                if (!dsConfig) dsConfig = dsFolderConfig;
                else DynamicConfigMap.mergeDynamicConfig(dsConfig, dsFolderConfig);
            }
            if (dsAppSiteConfig) {
                if (!dsConfig) dsConfig = dsAppSiteConfig;
                else DynamicConfigMap.mergeDynamicConfig(dsConfig, dsAppSiteConfig);
            }
            this._dynamicAppSiteConfigStore[site] = dsConfig;

            if (dsConfig) {
                for (const [, handlers] of dsConfig) {
                    if (!Array.isArray(handlers)) continue;
                    for (const handler of handlers) {
                        const paramFile = handler.iParamFile;
                        if (!paramFile) continue;
                        const storeKey = `${site}:${paramFile}`;
                        if (this._dynamicAppSiteParamsStore.containsKey(storeKey)) continue;
                        const fileName = paramFile.replace(/DynamicAppSite\./i, 'dynamicappsite.');
                        let paramJson = config.getDataJson(site, fileName) || config.getFolderDataJson(site, fileName);
                        if (!paramJson) {
                            const rootParam = config.getRootJson(fileName + '.json');
                            if (rootParam && rootParam.size > 0) paramJson = rootParam;
                        }
                        if (paramJson) this._dynamicAppSiteParamsStore.set(storeKey, JsonValue.object(paramJson));
                    }
                }
            }

            this._globalDataStore[site] = config.getDataJson(site, 'globaldata');

            const gvd = config.getDataJson(site, 'globalviewdata');
            if (gvd) {
                const resolvedGvd = new JsonObject();
                for (const [componentName, mappingVal] of gvd.entries()) {
                    const componentStoreKey = `${site}:${componentName.toLowerCase()}`;
                    let resolvedData: JsonObject | null = null;
                    if (mappingVal.isString && mappingVal.asString()) {
                        resolvedData = config.getDataJson(site, mappingVal.asString()!);
                    } else if (mappingVal.isObject) {
                        resolvedData = mappingVal.asObject();
                    }
                    if (resolvedData) {
                        resolvedGvd.set(componentName, resolvedData);
                        if (this._globalViewDataComponentStore[componentStoreKey] === undefined) {
                            this._globalViewDataComponentStore[componentStoreKey] = resolvedData;
                        }
                    }
                }
                this._globalViewDataStore[site] = resolvedGvd.size > 0 ? resolvedGvd : null;
            } else {
                this._globalViewDataStore[site] = null;
            }
        }
    },

    clearCache(this: LoaderPreProcess): void {
        Logger.debug(`[CLEARCACHE] Called, cache had ${this._appSiteCache.size} entries`, 'LoaderPreProcess');
        this._store.clear();
        this._dynamicDataConfigStore = {};
        this._dynamicClientDataConfigStore = {};
        this._dynamicViewConfigStore = {};
        this._dynamicActionConfigStore = {};
        this._dynamicAppSiteConfigStore = {};
        this._dynamicDataParamsStore = new JsonObject();
        this._dynamicClientDataParamsStore = new JsonObject();
        this._dynamicViewParamsStore = new JsonObject();
        this._dynamicActionParamsStore = new JsonObject();
        this._dynamicAppSiteParamsStore = new JsonObject();
        this._globalDataStore = {};
        this._globalViewDataStore = {};
        this._globalViewDataComponentStore = {};
        Logger.debug(`[CLEARCACHE] Cache cleared, now has ${this._appSiteCache.size} entries`, 'LoaderPreProcess');
    },

    ensureAppSiteLoaded(this: LoaderPreProcess, appSite: string): boolean {
        if (!appSite || !this._config) {
            return false;
        }
        return this.load(this._config, appSite);
    }
};
